import type { Filters } from '../../../types/AdvancedCrudManager'

/**
 * Normalizes a filter value into an array so value sets can be compared
 * regardless of whether the value is a scalar or already an array.
 */
function toValueArray(value: Filters['value']): unknown[] {
  if (Array.isArray(value)) {
    return value
  }
  if (value === null || value === undefined) {
    return []
  }
  return [value]
}

function uniq(values: unknown[]): unknown[] {
  return [...new Set(values)]
}

// Inclusion operators select values; exclusion operators reject them. The facet
// integrations (HeaderTabs / facet-distribution) emit `in`/`not_in`; the lister
// header emits `=`/`!=`, one filter per selected value.
const INCLUSION_OPERATORS = new Set(['in', '='])
const EXCLUSION_OPERATORS = new Set(['not_in', '!='])

function operator(filter: Filters): string {
  return (filter.operator ?? '').toLowerCase()
}

function groupByField(list: Filters[]): { byField: Map<string, Filters[]>; fieldless: Filters[] } {
  const byField = new Map<string, Filters[]>()
  const fieldless: Filters[] = []
  for (const filter of list) {
    if (!filter.field) {
      fieldless.push(filter)
      continue
    }
    const existing = byField.get(filter.field) ?? []
    existing.push(filter)
    byField.set(filter.field, existing)
  }
  return { byField, fieldless }
}

/**
 * Merges model-level forced filters with user-selected (facet / HeaderTabs)
 * filters, producing AT MOST ONE filter per field.
 *
 * Forced filters fall into two categories based on their operator:
 *
 * - Locking filters (`=`, `in`, …): the field is locked to the forced value;
 *   any user filter on that field is discarded so the user cannot escape the
 *   forced scope. Example: the invoice lister is locked to
 *   `transaction_type = invoice`.
 *
 * - Exclusion filters (`not_in`): the field is only narrowed, not locked. The
 *   user may still pick values via facets. A user `in` selection survives
 *   (minus anything the exclusion forbids — if that leaves nothing, the forced
 *   exclusion is kept); a user `not_in` selection is unioned with the
 *   exclusion. Example: the transaction lister excludes bills/invoices but
 *   still lets the user pick a transaction type.
 *
 * Collapsing every field to a single filter is deliberate: the backend ORs
 * two filters that share a field (instead of ANDing them), and persisted
 * lister state can accumulate duplicate forced filters — both are healed here.
 *
 * @param forced Model-level forced filters (from the lister config).
 * @param user   User-selected filters (facet distribution, HeaderTabs, …).
 */
export function resolveForcedFilters(forced: Filters[], user: Filters[]): Filters[] {
  const result: Filters[] = []

  const forcedGroups = groupByField(forced)
  const userGroups = groupByField(user)

  // Fieldless forced filters pass through untouched.
  result.push(...forcedGroups.fieldless)

  for (const [field, forcedFilters] of forcedGroups.byField) {
    const userFilters = userGroups.byField.get(field) ?? []
    userGroups.byField.delete(field)

    // A locking forced filter wins: emit it once, discard user input on the
    // field. Any sibling forced exclusion on the same field is redundant under
    // a lock and intentionally dropped.
    const [lockingFilter] = forcedFilters.filter((f) => !EXCLUSION_OPERATORS.has(operator(f)))
    if (lockingFilter) {
      result.push(lockingFilter)
      continue
    }

    // Exclusion-only forced filter(s): collapse the field to a single filter.
    const excluded = uniq(forcedFilters.flatMap((f) => toValueArray(f.value)))
    const userIn = uniq(
      userFilters.filter((f) => INCLUSION_OPERATORS.has(operator(f))).flatMap((f) => toValueArray(f.value)),
    )
    const userNotIn = uniq(
      userFilters.filter((f) => EXCLUSION_OPERATORS.has(operator(f))).flatMap((f) => toValueArray(f.value)),
    )

    // User filters on this field with any other operator (`like`, ranges, …) are
    // intentionally dropped: the backend ORs same-field filters, so they cannot
    // be ANDed onto the forced exclusion. The facet/header UIs only emit
    // `in`/`=`/`not_in`, so this is unreachable in practice.
    if (userIn.length > 0) {
      // User narrowed the list: keep the picks, minus anything the forced filter excludes.
      const narrowedIn = userIn.filter((v) => !excluded.includes(v))
      if (narrowedIn.length > 0) {
        result.push({ field, operator: 'in', value: narrowedIn })
      } else {
        // Every pick was forced-excluded: keep the forced exclusion rather than
        // emit an empty `in` (which would match nothing).
        result.push({ field, operator: 'not_in', value: excluded, forced: true })
      }
    } else if (userNotIn.length > 0) {
      // User also excludes values: union both exclusion sets. Still `forced` —
      // the field stays under the model-level exclusion.
      result.push({ field, operator: 'not_in', value: uniq([...excluded, ...userNotIn]), forced: true })
    } else {
      // No user input on the field: keep the forced exclusion.
      result.push({ field, operator: 'not_in', value: excluded, forced: true })
    }
  }

  // User filters on fields without any forced filter pass through unchanged.
  for (const [, list] of userGroups.byField) {
    result.push(...list)
  }
  result.push(...userGroups.fieldless)

  return result
}
